/**
 * HSE compliance tick — pure helper extracted from coreSlice.nextDay.
 *
 * 02/06/2026 — Pre-prod code review refactor : the daily HSE training
 * scan (FCO / medical / ADR / CACES) was inlined inside `nextDay`. This
 * helper returns the list of deferred memos to enqueue, leaving the
 * caller responsible for the actual queueing side-effect.
 *
 * Inputs : the post-fatigue driver array, the total-days reading (the
 * "tomorrow" value — i.e. state.totalDays + 1, so the threshold logic
 * matches the player's perception), and the hiredSupport map.
 *
 * Output :
 *   Array<{ dueDay: number, memo: MessagePayload, key: string }>
 *
 * Gated by Claire Maréchal being hired (she is the one auditing controls
 * and reporting). Returns [] otherwise.
 */

import { getCompanyHseCompliance, EXPIRING_WINDOW_DAYS, HSE_TRAININGS } from './hseCompliance';
import { buildTrainingExpiringMemo, buildTrainingExpiredMemo } from './hseAlertBuilder';

/**
 * @param {Array}  driversAfterLease - drivers post-fatigue / lease detach
 * @param {number} totalDays - state.totalDays (pre-flip)
 * @param {Object} hiredSupport - state.hiredSupport
 * @returns {Array<{dueDay: number, memo: object, key: string}>}
 */
export function runHseComplianceTick(driversAfterLease, totalDays, hiredSupport) {
  if (!(hiredSupport || {})['role-claire-marechal']) return [];
  const tomorrowTotal = (totalDays || 0) + 1;
  const compliance = getCompanyHseCompliance(driversAfterLease, tomorrowTotal);
  const deferred = [];
  const enqueueDay = (totalDays || 0) + 1;

  compliance.details.forEach(detail => {
    Object.entries(detail.trainings).forEach(([trainingKey, t]) => {
      if (!HSE_TRAININGS[trainingKey] || t.applicable === false) return;
      if (t.status === 'expiring' && t.daysRemaining === EXPIRING_WINDOW_DAYS) {
        deferred.push({
          dueDay: enqueueDay,
          memo: buildTrainingExpiringMemo({
            trainingKey,
            driverName: detail.name,
            daysRemaining: t.daysRemaining,
            expiresOn: t.expiresOn,
          }),
          key: `claire-${trainingKey}-expiring-${detail.driverId}-${t.expiresOn}`,
        });
      }
      if (t.status === 'expired' && t.daysRemaining === -1) {
        deferred.push({
          dueDay: enqueueDay,
          memo: buildTrainingExpiredMemo({
            trainingKey,
            driverName: detail.name,
            expiresOn: t.expiresOn,
          }),
          key: `claire-${trainingKey}-expired-${detail.driverId}-${t.expiresOn}`,
        });
      }
    });
  });

  return deferred;
}
